import { useCallback, useEffect, useMemo, useState } from "react"
import { useToasts } from "../../contexts/toasts";
import { useFullScreenLoader } from "../../contexts/fullScreenLoader";
import { Badge, Breadcrumb, Button, Col, Container, Modal, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Alert, Flex, Space, Table, FormProps, Form, Input, Tag, Select, ColorPicker } from "antd";
import AppSearch from "../../components/common/AppSearch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import IconButton from "../../components/common/IconButton";
import { Categories } from "../../api/Categories";
import AppLoader from "../../components/common/AppLoader";
import { AssetCategory, Option } from "../../models/generics";
import { CATEGORY_ICONS } from "../../components/common/AssetCategory";
import { IconName } from "@fortawesome/fontawesome-svg-core";

const formLayout: FormProps = {
  layout: "horizontal",
  colon: false,
  labelCol: { span: 4 },
}

export default function CategoriesManagement() {
  const [form] = Form.useForm();
  const [error, setError] = useState(false);
  const [categories, setCategories] = useState<AssetCategory[]>();
  const [selectedCategories, setSelectedCategories] = useState<AssetCategory[]>([]);
  const [category, setCategory] = useState<AssetCategory>();
  const [searchText, setSearchText] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [targetsOptions, setTargetsOptions] = useState<Option[]>();

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const getCategories = useCallback(() => {
    const promise = selectedCategories.length === 0
      ? Categories.getCategories()
      : Categories.getCategory(selectedCategories[selectedCategories.length - 1].id!);

    promise
      .then(
        ({ data }) => {
          if (selectedCategories.length === 0) setCategories(data as AssetCategory[]);
          else setCategories((data as AssetCategory).children);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('categories-error', message, `Categories Error ${status ? `(${status})` : ''}`, 'danger');
          setError(true);
        }
      );
  }, [showToast, selectedCategories]);

  const getCategoriesTargets = useCallback(() => {
    Categories.getCategoriesTargets()
      .then(
        ({ data }) => setTargetsOptions(data.map(target => ({ label: target, value: target }))),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('categories-targets-error', message, `Categories Targets Error ${status ? `(${status})` : ''}`, 'danger');
          setTargetsOptions([]);
        }
      )
  }, [showToast]);

  const reset = useCallback(() => {
    setError(false);
    setSearchText("");
    setCategories(undefined);
    getCategories();
    getCategoriesTargets();
  }, [getCategories, getCategoriesTargets]);

  useEffect(reset, [reset, selectedCategories]);

  const handleSelectCategory = useCallback((category: AssetCategory) => () => {
    setSelectedCategories(curr => (curr || []).concat(category));
  }, []);

  const openCreateModal = useCallback(() => {
    if (selectedCategories) {
      const parentTarget = selectedCategories.at(-1)?.target;
      form.setFieldValue("target", parentTarget);
    }
    setShowEditModal(true);
  }, [selectedCategories, form]);

  const handleEdit = useCallback((item: AssetCategory) => () => {
    setCategory(item);
    form.setFieldsValue(item);
    setShowEditModal(true);
  }, [form]);
  const openDeleteModal = useCallback((item: AssetCategory) => () => {
    setCategory(item);
    setShowDeleteModal(true);
  }, []);
  const handleDeleteModalClose = useCallback(() => {
    setCategory(undefined);
    setShowDeleteModal(false);
  }, []);
  const handleEditModalClose = useCallback(() => {
    setShowEditModal(false);
    setCategory(undefined);
    form.resetFields();
  }, [form]);

  const filteredCategories = useMemo(() => categories?.filter(category => category.name.toLowerCase().includes(searchText.toLowerCase())), [categories, searchText]);

  const handleSubmit = useCallback((values: any) => {
    if (values.hexColor && typeof values.hexColor !== 'string') values.hexColor = values.hexColor.toHexString();

    showLoader();

    const body = {
      ...values,
      fatherId: selectedCategories[selectedCategories.length - 1]?.id || null
    }

    const promise = category !== undefined
      ? Categories.updateCategory(category!.id!, body)
      : Categories.createCategory(body);

    promise.then(
      () => {
        hideLoader();
        showToast('category-update-success', `Category ${category ? 'updated' : 'created'} successfully`, 'Category Success', 'success');
        handleEditModalClose();
        reset();
      },
      (err) => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('category-update-error', message, `Category Error ${status ? `(${status})` : ''}`, 'danger');
      }
    )
  }, [category, showLoader, hideLoader, reset, showToast, selectedCategories, handleEditModalClose]);

  const deleteCategory = useCallback(() => {
    showLoader();

    Categories.deleteCategory(category!.id!)
      .then(
        () => {
          hideLoader();
          showToast('category-delete-success', 'Category deleted successfully', 'Category Delete Success', 'success');
          handleDeleteModalClose();
          reset();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('category-delete-error', message, `Category Delete Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [category, showLoader, hideLoader, showToast, reset, handleDeleteModalClose]);

  const handleRemoveCategory = useCallback((index: number) => () => {
    setSelectedCategories(curr => curr!.slice(0, index));
  }, []);

  const disabledTargetInput = useMemo(() => selectedCategories.length > 0 || !!category, [selectedCategories, category]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert
        type="error"
        showIcon
        message="An error occurred while retrieving Categories. Try again later."
      />
    )

    if (!filteredCategories) return <AppLoader className="py-5 my-5" justify="center" size={100} />;

    return (
      <Table
        size="middle"
        scroll={{ y: 'calc(100vh - 300px)' }}
        dataSource={filteredCategories}
        rowKey="id"
      >
        <Table.Column
          key="icon"
          dataIndex="icon"
          // title="Name"
          width="35px"
          className="text-center"
          render={(icon: IconName) => icon && <FontAwesomeIcon icon={icon} size="lg" fixedWidth />}
        />
        <Table.Column
          key="key"
          dataIndex="name"
          title="Name"
          // width="30%"
          render={(_, record: AssetCategory) => <Link to="#" onClick={handleSelectCategory(record)}>{record.name}</Link>}
        />
        <Table.Column
          key="target"
          dataIndex="target"
          title="Target"
          width="10%"
          className="text-center"
          render={value => <Tag color="blue" className="fw-bold text-uppercase rounded-pill px-2">{value}</Tag>}
        />
        <Table.Column
          key="hexColor"
          dataIndex="hexColor"
          title="Color"
          width="10%"
          className="text-center"
          render={color => {
            return color && (
              <Tag className="fw-bold" style={{ color }}>
                <FontAwesomeIcon icon={solid('circle')} />
                <span className="ms-1 text-uppercase">{color}</span>
              </Tag>
            );
          }}
        />
        <Table.Column
          key="actions"
          title="Actions"
          className="text-center"
          width="10%"
          render={(_value, record: AssetCategory) => (
            <Space size={5}>
              <IconButton tooltip="Edit" onClick={handleEdit(record)}>
                <FontAwesomeIcon fixedWidth icon={solid('pen')} />
              </IconButton>
              <IconButton tooltip="Delete" className="text-danger" onClick={openDeleteModal(record)}>
                <FontAwesomeIcon fixedWidth icon={solid('trash')} />
              </IconButton>
            </Space>
          )}
        />
      </Table>
    )
  }, [error, filteredCategories, handleEdit, openDeleteModal, handleSelectCategory]);

  return (
    <Container className="my-3">
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/control-panel" }}>Control Panel</Breadcrumb.Item>
        <Breadcrumb.Item active>Categories Management</Breadcrumb.Item>
      </Breadcrumb>

      <Row className="mb-3 align-items-center">
        <Col>
          <Flex align="center">
            <h5 className="mb-0">Categories</h5>
            {selectedCategories.map((category, index) => (
              <Flex key={index} align="center">
                <FontAwesomeIcon icon={solid('chevron-right')} size="xs" className="mx-2" transform="down-2" />
                <Badge key={category.id!} bg="light" className="d-flex align-items-center border text-dark" pill>
                  <span className="me-2 pe-1">{category.name}</span>
                  <IconButton className="p-0" onClick={handleRemoveCategory(index)}>
                    <FontAwesomeIcon icon={solid('times')} />
                  </IconButton>
                </Badge>
              </Flex>
            ))}
          </Flex>
        </Col>

        <Col className="text-center px-3">
          <AppSearch value={searchText} placeholder={`Search ${selectedCategories.length > 0 ? 'sub-' : ''}category...`} onChange={setSearchText} />
        </Col>

        <Col md={3} className="text-end">
          <Button size="sm" variant="success" onClick={openCreateModal}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="ms-2">Register {selectedCategories.length > 0 ? 'Sub-' : ''}Category</span>
          </Button>
        </Col>
      </Row>

      {renderAsyncContent()}

      <Modal show={showDeleteModal} onHide={handleDeleteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete category <b>{category?.name}</b>?</p>

          <Alert type="warning" showIcon message="Remember, this operation cannot be undone" />
        </Modal.Body>
        <Modal.Footer>
          <Flex justify="space-between" className="mt-3">
            <Button size="sm" variant="light" className="border" onClick={handleDeleteModalClose}>Cancel</Button>
            <Button size="sm" className="ms-2" variant="danger" onClick={deleteCategory}>
              <FontAwesomeIcon icon={solid('trash')} />
              <span className="ms-2">Delete</span>
            </Button>
          </Flex>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={handleEditModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{`${category?.id ? 'Edit' : 'Create'} ${selectedCategories.length > 0 ? 'Sub-' : ''}Category`}</Modal.Title>
        </Modal.Header>
        <Form className="compact-form" form={form} {...formLayout} onFinish={handleSubmit}>
          <Modal.Body>
            <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Category name is required.' }]}>
              <Input placeholder="Category Name" />
            </Form.Item>
            <Form.Item name="target" label="Target" rules={[{ required: true, message: 'Category target is required.' }]}>
              <Select options={targetsOptions} disabled={disabledTargetInput} loading={!targetsOptions} placeholder="Category Target" />
            </Form.Item>
            <Form.Item name="icon" label="Icon">
              <Select placeholder="Category Icon" allowClear>
                {CATEGORY_ICONS.map((icon, index) => (
                  <Select.Option key={index} value={icon}>
                    <FontAwesomeIcon icon={icon} />
                    <span className="ms-2">{icon}</span>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="hexColor" label="Color">
              <ColorPicker size="small" showText getPopupContainer={trigger => trigger.parentElement ?? document.body} />
            </Form.Item>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <Button type="button" variant="light" size="sm" className="border" onClick={handleEditModalClose}>Close</Button>
            <Button type="submit" variant="success" size="sm">
              <FontAwesomeIcon icon={solid('save')} />
              <span className="ms-2">Save</span>
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  )
}